"use client";

import { type FormEvent, useEffect, useMemo, useState } from "react";

import { ApiRequestError } from "@/lib/auth-api";
import { loadCrossBorderDetails, updateCrossBorderDetails } from "@/lib/order-api";
import type { AmountStatus, CrossBorderDetails, CrossBorderUpdateInput, Incoterm, ResponsibilityParty } from "@/lib/order-types";

interface CrossBorderPanelProps {
    orderId: string;
    csrfToken: string | null;
    roles: string[];
}

interface FormState {
    destinationCountryCode: string;
    incoterm: Incoterm;
    taxResponsibility: ResponsibilityParty;
    dutyResponsibility: ResponsibilityParty;
    importerOfRecord: ResponsibilityParty;
    taxAmountStatus: AmountStatus;
    taxMinor: string;
    dutyAmountStatus: AmountStatus;
    dutyMinor: string;
    amountCurrency: string;
}

const responsibilityOptions: { value: ResponsibilityParty; label: string }[] = [
    { value: "MERCHANT", label: "商户" },
    { value: "CUSTOMER", label: "客户" },
    { value: "LOGISTICS_PROVIDER", label: "物流服务商" },
    { value: "TO_BE_CONFIRMED", label: "待确认" },
];

const amountStatusOptions: { value: AmountStatus; label: string }[] = [
    { value: "PENDING", label: "待确认（金额未知）" },
    { value: "CONFIRMED", label: "已确认" },
    { value: "NOT_APPLICABLE", label: "不适用" },
];

const valueSourceLabels = { SOURCE: "来源系统", TEMPLATE: "规则模板", MANUAL: "手工覆盖" } as const;
const writeRoles = new Set(["ADMIN", "ORDER_OPERATOR", "FINANCE", "CUSTOMS"]);

export function CrossBorderPanel({ orderId, csrfToken, roles }: CrossBorderPanelProps) {
    const [details, setDetails] = useState<CrossBorderDetails | null>(null);
    const [form, setForm] = useState<FormState | null>(null);
    const [reason, setReason] = useState("");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        let active = true;
        void loadCrossBorderDetails(orderId)
            .then((data) => {
                if (!active) return;
                setDetails(data);
                setForm(formFromDetails(data));
            })
            .catch((caught: unknown) => {
                if (active) setError(crossBorderErrorMessage(caught));
            })
            .finally(() => {
                if (active) setIsLoading(false);
            });
        return () => {
            active = false;
        };
    }, [orderId]);

    const changedFields = useMemo(() => (details && form ? collectChangedFields(details, form) : []), [details, form]);
    const roleCanWrite = roles.some((role) => writeRoles.has(role));
    const canWrite = Boolean(details && form && !details.locked && roleCanWrite && csrfToken);

    async function submit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        if (!details || !form || !csrfToken) return;
        if (reason.trim().length < 2) {
            setError("请填写至少 2 个字符的变更原因");
            return;
        }
        let input: CrossBorderUpdateInput;
        try {
            input = buildUpdateInput(details, form, reason);
        } catch (caught) {
            setError(caught instanceof Error ? caught.message : "表单内容不合法");
            return;
        }
        setIsSaving(true);
        setError(null);
        setSuccess(null);
        try {
            const updated = await updateCrossBorderDetails(orderId, input, csrfToken);
            setDetails(updated);
            setForm(formFromDetails(updated));
            setReason("");
            setSuccess(updated.value_source === "TEMPLATE" ? "默认模板已应用并留存审计记录" : "跨境字段已保存并留存审计记录");
        } catch (caught) {
            setError(crossBorderErrorMessage(caught));
        } finally {
            setIsSaving(false);
        }
    }

    if (isLoading) {
        return (
            <section className="cross-border-panel" aria-label="跨境贸易信息">
                <div className="cross-border-loading" role="status">
                    正在读取跨境规则…
                </div>
            </section>
        );
    }

    if (!details || !form) {
        return (
            <section className="cross-border-panel" aria-label="跨境贸易信息">
                <div className="cross-border-error" role="alert">
                    <strong>暂时无法显示跨境信息</strong>
                    <span>{error}</span>
                </div>
            </section>
        );
    }

    return (
        <section className="cross-border-panel" aria-label="跨境贸易信息">
            <div className="order-section-title cross-border-title">
                <div>
                    <h3>跨境贸易信息</h3>
                    <small>
                        {details.origin_country_code} → {details.destination_country_code} · {details.incoterm}
                    </small>
                </div>
                <span className={`cross-border-source ${details.value_source.toLowerCase()}`}>
                    {valueSourceLabels[details.value_source]}
                </span>
            </div>
            <div className="cross-border-meta">
                <span>规则版本 {details.rule_version}</span>
                <span>订单版本 {details.order_version}</span>
                <span>关务状态 {customsStatusLabel(details.customs_status)}</span>
                <span>{details.persisted ? "已保存快照" : "模板预览，尚未保存"}</span>
            </div>
            {details.locked && <div className="form-message warning">订单已支付或已申报，跨境字段现为只读。</div>}
            {!roleCanWrite && <div className="form-message warning">当前角色仅可查看，不能修改跨境字段。</div>}
            {!csrfToken && <div className="form-message warning">当前会话缺少写入令牌，请退出后重新登录再编辑。</div>}
            <form className="cross-border-form" onSubmit={submit}>
                <div className="cross-border-grid">
                    <Field label="目的国家/地区">
                        <input
                            disabled={!canWrite}
                            maxLength={2}
                            minLength={2}
                            onChange={(event) => setForm({ ...form, destinationCountryCode: event.target.value.toUpperCase() })}
                            pattern="[A-Z]{2}"
                            required
                            value={form.destinationCountryCode}
                        />
                    </Field>
                    <Field label="贸易术语">
                        <select
                            disabled={!canWrite}
                            onChange={(event) => setForm({ ...form, incoterm: event.target.value as Incoterm })}
                            value={form.incoterm}
                        >
                            <option value="DDP">DDP · 完税后交货</option>
                            <option value="DAP">DAP · 目的地交货</option>
                        </select>
                    </Field>
                    <ResponsibilityField
                        disabled={!canWrite}
                        label="税费承担方"
                        onChange={(value) => setForm({ ...form, taxResponsibility: value })}
                        value={form.taxResponsibility}
                    />
                    <ResponsibilityField
                        disabled={!canWrite}
                        label="关税承担方"
                        onChange={(value) => setForm({ ...form, dutyResponsibility: value })}
                        value={form.dutyResponsibility}
                    />
                    <ResponsibilityField
                        disabled={!canWrite}
                        label="进口申报主体"
                        onChange={(value) => setForm({ ...form, importerOfRecord: value })}
                        value={form.importerOfRecord}
                    />
                    <Field label="金额币种">
                        <input
                            disabled={!canWrite}
                            maxLength={3}
                            minLength={3}
                            onChange={(event) => setForm({ ...form, amountCurrency: event.target.value.toUpperCase() })}
                            pattern="[A-Z]{3}"
                            required
                            value={form.amountCurrency}
                        />
                    </Field>
                    <AmountField
                        currency={form.amountCurrency}
                        disabled={!canWrite}
                        label="税费"
                        minor={form.taxMinor}
                        onMinorChange={(value) => setForm({ ...form, taxMinor: value })}
                        onStatusChange={(value) =>
                            setForm({ ...form, taxAmountStatus: value, taxMinor: value === "CONFIRMED" ? form.taxMinor : "" })
                        }
                        status={form.taxAmountStatus}
                    />
                    <AmountField
                        currency={form.amountCurrency}
                        disabled={!canWrite}
                        label="关税"
                        minor={form.dutyMinor}
                        onMinorChange={(value) => setForm({ ...form, dutyMinor: value })}
                        onStatusChange={(value) =>
                            setForm({ ...form, dutyAmountStatus: value, dutyMinor: value === "CONFIRMED" ? form.dutyMinor : "" })
                        }
                        status={form.dutyAmountStatus}
                    />
                </div>
                {canWrite && (
                    <div className="cross-border-submit">
                        <label>
                            <span>变更原因（审计必填）</span>
                            <textarea
                                maxLength={500}
                                minLength={2}
                                onChange={(event) => setReason(event.target.value)}
                                placeholder={details.persisted ? "说明本次手工覆盖原因" : "说明应用默认模板的原因"}
                                required
                                value={reason}
                            />
                        </label>
                        <div className="change-summary">
                            <strong>{changedFields.length ? `将变更：${changedFields.join("、")}` : "未修改模板字段"}</strong>
                            <span>{changedFields.length ? "提交后来源将标记为手工覆盖" : "提交后保存当前模板快照"}</span>
                        </div>
                        <button
                            className="button primary"
                            disabled={isSaving || (details.persisted && changedFields.length === 0)}
                            type="submit"
                        >
                            {isSaving
                                ? "保存中…"
                                : changedFields.length
                                  ? "保存手工覆盖"
                                  : details.persisted
                                    ? "暂无变更"
                                    : "应用模板默认值"}
                        </button>
                    </div>
                )}
                {error && (
                    <div className="form-message error" role="alert">
                        {error}
                    </div>
                )}
                {success && (
                    <div className="form-message success" role="status">
                        {success}
                    </div>
                )}
            </form>
        </section>
    );
}

function Field({ children, label }: { children: React.ReactNode; label: string }) {
    return (
        <label>
            <span>{label}</span>
            {children}
        </label>
    );
}

function ResponsibilityField({
    disabled,
    label,
    onChange,
    value,
}: {
    disabled: boolean;
    label: string;
    onChange: (value: ResponsibilityParty) => void;
    value: ResponsibilityParty;
}) {
    return (
        <Field label={label}>
            <select disabled={disabled} onChange={(event) => onChange(event.target.value as ResponsibilityParty)} value={value}>
                {responsibilityOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </Field>
    );
}

function AmountField({
    currency,
    disabled,
    label,
    minor,
    onMinorChange,
    onStatusChange,
    status,
}: {
    currency: string;
    disabled: boolean;
    label: string;
    minor: string;
    onMinorChange: (value: string) => void;
    onStatusChange: (value: AmountStatus) => void;
    status: AmountStatus;
}) {
    return (
        <div className="amount-field">
            <Field label={`${label}状态`}>
                <select disabled={disabled} onChange={(event) => onStatusChange(event.target.value as AmountStatus)} value={status}>
                    {amountStatusOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>
            </Field>
            {status === "CONFIRMED" && (
                <Field label={`${label}金额（${currency} 最小货币单位）`}>
                    <input
                        disabled={disabled}
                        min="0"
                        onChange={(event) => onMinorChange(event.target.value)}
                        required
                        step="1"
                        type="number"
                        value={minor}
                    />
                </Field>
            )}
        </div>
    );
}

function formFromDetails(details: CrossBorderDetails): FormState {
    return {
        destinationCountryCode: details.destination_country_code,
        incoterm: details.incoterm,
        taxResponsibility: details.tax_responsibility,
        dutyResponsibility: details.duty_responsibility,
        importerOfRecord: details.importer_of_record,
        taxAmountStatus: details.tax_amount_status,
        taxMinor: details.tax_minor === null ? "" : String(details.tax_minor),
        dutyAmountStatus: details.duty_amount_status,
        dutyMinor: details.duty_minor === null ? "" : String(details.duty_minor),
        amountCurrency: details.amount_currency,
    };
}

export function collectChangedFields(details: CrossBorderDetails, form: FormState): string[] {
    const fields: [string, unknown, unknown][] = [
        ["目的国家/地区", details.destination_country_code, form.destinationCountryCode],
        ["贸易术语", details.incoterm, form.incoterm],
        ["税费承担方", details.tax_responsibility, form.taxResponsibility],
        ["关税承担方", details.duty_responsibility, form.dutyResponsibility],
        ["进口申报主体", details.importer_of_record, form.importerOfRecord],
        ["税费状态", details.tax_amount_status, form.taxAmountStatus],
        ["税费金额", details.tax_minor === null ? "" : String(details.tax_minor), form.taxMinor],
        ["关税状态", details.duty_amount_status, form.dutyAmountStatus],
        ["关税金额", details.duty_minor === null ? "" : String(details.duty_minor), form.dutyMinor],
        ["金额币种", details.amount_currency, form.amountCurrency],
    ];
    return fields.filter(([, before, after]) => before !== after).map(([label]) => label);
}

function buildUpdateInput(details: CrossBorderDetails, form: FormState, reason: string): CrossBorderUpdateInput {
    const input: CrossBorderUpdateInput = {
        expected_version: details.order_version,
        idempotency_key: `xb-ui-${orderKey(details.order_id)}-${Date.now()}`,
        reason: reason.trim(),
    };
    if (details.destination_country_code !== form.destinationCountryCode) input.destination_country_code = form.destinationCountryCode;
    if (details.incoterm !== form.incoterm) input.incoterm = form.incoterm;
    if (details.tax_responsibility !== form.taxResponsibility) input.tax_responsibility = form.taxResponsibility;
    if (details.duty_responsibility !== form.dutyResponsibility) input.duty_responsibility = form.dutyResponsibility;
    if (details.importer_of_record !== form.importerOfRecord) input.importer_of_record = form.importerOfRecord;
    if (details.amount_currency !== form.amountCurrency) input.amount_currency = form.amountCurrency;
    addAmountChanges(input, "tax", details.tax_amount_status, details.tax_minor, form.taxAmountStatus, form.taxMinor);
    addAmountChanges(input, "duty", details.duty_amount_status, details.duty_minor, form.dutyAmountStatus, form.dutyMinor);
    return input;
}

function addAmountChanges(
    input: CrossBorderUpdateInput,
    key: "tax" | "duty",
    oldStatus: AmountStatus,
    oldMinor: number | null,
    newStatus: AmountStatus,
    newMinorText: string,
) {
    const newMinor = newStatus === "CONFIRMED" ? parseMinorAmount(newMinorText) : null;
    if (oldStatus === newStatus && oldMinor === newMinor) return;
    if (key === "tax") {
        input.tax_amount_status = newStatus;
        input.tax_minor = newMinor;
    } else {
        input.duty_amount_status = newStatus;
        input.duty_minor = newMinor;
    }
}

function parseMinorAmount(value: string): number {
    if (!/^\d+$/.test(value)) throw new Error("已确认的税费或关税必须填写非负整数金额");
    const amount = Number(value);
    if (!Number.isSafeInteger(amount)) throw new Error("金额超出安全范围");
    return amount;
}

function orderKey(orderId: string): string {
    return orderId.replace(/-/g, "").slice(0, 12);
}

function customsStatusLabel(status: CrossBorderDetails["customs_status"]): string {
    return {
        NOT_SUBMITTED: "未申报",
        SUBMITTED: "已提交",
        ACCEPTED: "已受理",
        RELEASED: "已放行",
        REJECTED: "已拒绝",
    }[status];
}

function crossBorderErrorMessage(error: unknown): string {
    if (!(error instanceof ApiRequestError)) return "读取跨境信息失败";
    const messages: Record<string, string> = {
        AUTH_REQUIRED: "登录状态已失效，请重新登录",
        SESSION_INVALID: "登录状态已失效，请重新登录",
        CROSS_BORDER_TEMPLATE_NOT_FOUND: "该订单渠道尚未配置适用的跨境规则模板",
        CROSS_BORDER_DETAILS_LOCKED: "订单已支付或已申报，不能再修改跨境字段",
        ORDER_VERSION_CONFLICT: "订单已被其他操作更新，请重新选择订单后再提交",
        FIELD_PERMISSION_DENIED: "当前角色无权修改所选字段",
        PERMISSION_DENIED: "当前角色无跨境字段写入权限",
        CSRF_INVALID: "写入令牌已失效，请退出后重新登录",
    };
    return messages[error.errorCode] ?? error.message;
}
